import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import * as THREE from "three";
import { useThree } from "@react-three/fiber";
import DraggableObject from "./DraggableObject";
import { DoorType, WallType } from "../../types/floorplannerTypes";
import { SelectableSymbol, BoundingBox } from './SelectableSymbol';
import { projectToWorld } from "./projectToWorld";
import { FloorplannerStoreContext } from "../../store/floorplannerStore";
import AttachableSymbol from "./AttachableSymbol";
import { Line } from "@react-three/drei";
import { transaction } from "mobx";
import { editorStore } from "../../store/editorStore";
import { observer } from "mobx-react-lite";
import { AttachmentMeasurementLines } from "./AttachmentMeasurementLines";
import { getSymbolRenderTotalWidth } from "../../utils/symbolAttachmentGeometry";
import {
  getWallAttachmentRenderOrder,
  getWallAttachmentZOffset,
  usePlanarWallAttachmentLayer,
} from "./wallAttachmentRenderLayer";
import { CANVAS_ACCENT_COLOR, canvasAccentColor, canvasHandleFillColor } from "../../constants/brandColors";
import { resolveFillOpacity } from "../../utils/fillOpacity";
import { getOpacityRemountEpoch } from "../../utils/opacityRemount";
import ObjectOpacityGroup from "./ObjectOpacityGroup";

export const defaultOpenAngle = Math.PI / 2;
export const defaultDoorWidth = 1;

interface DoorProps {
  door: DoorType;
  attachedToWall?: WallType;
  onDragStart: (door: DoorType, offset: [number, number]) => void;
  onDrag: (newPosition: [number, number]) => void;
  onDragEnd: (endPosition: [number, number]) => void;
}

const Door: React.FC<DoorProps> = observer(({
  door,
  attachedToWall,
  onDragStart,
  onDragEnd,
  onDrag,
}) => {
  const [currentOpenAngle, setCurrentOpenAngle] = useState(door.openAngle);
  const currentOpenAngleRef = useRef(currentOpenAngle); // Ref for the current open angle in event listeners
  const { gl, camera } = useThree();
  const groupRef = useRef<THREE.Group>(null);
  const wallAttachmentLayerRef = useRef<THREE.Group>(null);
  const attachableRef = useRef<{ checkForAttachment: () => void }>(null);
  const floorplannerStore = React.useContext(FloorplannerStoreContext);
  const isAttachedToWall = Boolean(attachedToWall);
  const wallAttachmentRenderOrder = getWallAttachmentRenderOrder(door.zIndex);

  usePlanarWallAttachmentLayer(
    wallAttachmentLayerRef,
    isAttachedToWall,
    wallAttachmentRenderOrder,
  );

  const setWallAttachmentLayerRef = useCallback((node: THREE.Group | null) => {
    groupRef.current = node;
    wallAttachmentLayerRef.current = node;
  }, []);

  const lineWeight = door.lineWeight || floorplannerStore.doorLineWeight; // Thickness of the lines
  const frameThickness = door.doorFrameWidth || floorplannerStore.doorFrameWidth; // Thickness of the door frame
  const bladeThickness = door.doorBladeThickness || floorplannerStore.doorBladeThickness; // Thickness of the door blade
  const [doorWidth, setDoorWidth] = useState(door.doorWidth); // Width of the door
  const grabHandleSize = 0.12;
  const handleRadius = useMemo(() => grabHandleSize / editorStore.zoomLevelDivisor(), [editorStore.zoomLevel]);
  const handleDepth = 0.02; // Depth of the handles

  const flipX = door.flipHorizontal ? -1 : 1;
  const flipY = door.flipVertical ? -1 : 1;
  const [centerPosition, setCenterPosition] = useState<[number, number]>([
    doorWidth / 2,
    doorWidth / 2,
  ]);
  const lineColor = door.lineColor || floorplannerStore.lineColor;
  const wallWidth = attachedToWall?.wallWidth || floorplannerStore.wallWidth;
  const wallLineWeight = floorplannerStore.convertLineWeightToWorld(attachedToWall?.lineWeight || floorplannerStore.lineWeight);

  const wallLineColor = attachedToWall?.lineColor || floorplannerStore.lineColor;
  const doorFrameWidth = door.doorFrameWidth || floorplannerStore.doorFrameWidth;
  const attachment = attachedToWall?.symbolAttachments?.find((item) => item.symbolId === door.id);
  const renderedDoorWidth = useMemo(() => {
    if (!attachedToWall || !attachment) {
      return doorWidth;
    }
    const openingWidth = getSymbolRenderTotalWidth(
      attachedToWall,
      door,
      attachment.positionFromStart,
      floorplannerStore.doorFrameWidth,
    );
    return Math.max(openingWidth - frameThickness * 2, 0.01);
  }, [attachedToWall, attachment?.positionFromStart, door, doorWidth, floorplannerStore.doorFrameWidth, frameThickness]);
  const hingePosition = useMemo(() => {
    return door.flipHorizontal
      ? attachedToWall ? [renderedDoorWidth, (wallLineWeight / 2 + bladeThickness / 2) * flipY, 0] : [renderedDoorWidth, 0, 0]
      : attachedToWall ? [0, (wallLineWeight / 2 + bladeThickness / 2) * flipY, 0] : [0, 0, 0];
  }, [attachedToWall, bladeThickness, door.flipHorizontal, flipY, renderedDoorWidth, wallLineWeight]);

  const hingeOffsetX = useMemo(() => (flipX === -1 ? renderedDoorWidth : 0), [flipX, renderedDoorWidth]);
  const openAngleGrabHandleX = useMemo(() => hingeOffsetX + renderedDoorWidth * 0.6 * Math.cos(currentOpenAngle) * flipX, [currentOpenAngle, flipX, hingeOffsetX, renderedDoorWidth]);
  const openAngleGrabHandleY = useMemo(() => renderedDoorWidth * 0.6 * Math.sin(currentOpenAngle) * flipY, [currentOpenAngle, flipY, renderedDoorWidth]);
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingAngle, setIsDraggingAngle] = useState(false);
  const [hoveringHandle, setHoveringHandle] = useState<string | null>(null);
  const noopRaycast = () => null;
  const aboutToModify = useRef(false);
  const selectionHandlesVisible = editorStore.shouldShowSelectionHandles();

  const calculateBoundingBox = useCallback((): BoundingBox => {
    if (!groupRef.current) return { topLeft: [0, 0], topRight: [0, 0], bottomLeft: [0, 0], bottomRight: [0, 0], width: 0, height: 0, depth: 0 };

    // Calculate the actual bounds of the door symbol based on the open angle and flip state
    // The door has:
    // 1. The door frame (if attached to wall)
    // 2. The arc that shows the door swing
    // 3. The blade at the current angle

    // Calculate how far the arc extends based on the current open angle
    const arcExtentX = renderedDoorWidth * Math.cos(currentOpenAngle) * flipX; // How far blade tip extends in X
    const arcExtentY = renderedDoorWidth * Math.sin(currentOpenAngle) * flipY; // How far blade tip extends in Y

    // The hinge is at x=0 (or x=doorWidth if flipHorizontal)
    const hingeX = door.flipHorizontal ? renderedDoorWidth : 0;

    // Calculate the X bounds
    let minX: number, maxX: number;
    if (door.flipHorizontal) {
      // Hinge on right, door opens to the left
      minX = Math.min(0, hingeX + renderedDoorWidth * Math.cos(currentOpenAngle) * flipX);
      maxX = renderedDoorWidth;
    } else {
      // Hinge on left, door opens to the right  
      minX = 0;
      maxX = Math.max(renderedDoorWidth, hingeX + renderedDoorWidth * Math.cos(currentOpenAngle) * flipX);
    }

    // Calculate the Y bounds based on flip state
    // The arc extends based on the current open angle, not the full door width
    // Use Math.abs(arcExtentY) since arcExtentY already includes flipY direction
    let minY: number, maxY: number;
    if (door.flipVertical) {
      // Door swings downward (negative Y)
      minY = -Math.abs(arcExtentY);
      maxY = bladeThickness; // Small amount for the blade thickness at hinge
    } else {
      // Door swings upward (positive Y)
      minY = -bladeThickness; // Small amount for the blade thickness
      maxY = Math.abs(arcExtentY);
    }

    // Adjust bounds if attached to wall (add frame thickness)
    if (attachedToWall) {
      minX -= frameThickness + wallLineWeight;
      maxX += frameThickness + wallLineWeight;
      // The wall width extends equally above and below the door line
      const halfWallWidth = wallWidth / 2 + wallLineWeight;
      if (door.flipVertical) {
        minY = Math.min(minY, -halfWallWidth);
        maxY = Math.max(maxY, halfWallWidth);
      } else {
        minY = Math.min(minY, -halfWallWidth);
        maxY = Math.max(maxY, halfWallWidth);
      }
    }

    const width = maxX - minX;
    const height = maxY - minY;

    // Center position is the center of the bounding box
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    setCenterPosition([centerX, centerY]);

    return {
      topLeft: [minX, maxY],
      topRight: [maxX, maxY],
      bottomLeft: [minX, minY],
      bottomRight: [maxX, minY],
      width: width,
      height: height,
      depth: 0,
    };
  }, [attachedToWall, bladeThickness, currentOpenAngle, door.flipHorizontal, door.flipVertical, flipX, flipY, frameThickness, renderedDoorWidth, wallLineWeight, wallWidth]);

  const dragOffset = useRef({ x: 0, y: 0 });

  const onHandleDrag = (event: PointerEvent) => {
    if (aboutToModify.current) {
      floorplannerStore.beginUndoTransaction('updateOpenAngle');
      floorplannerStore.recordSymbolForUndo(door.id);
      aboutToModify.current = false;
    }
    const [worldX, worldY] = projectToWorld(event.clientX, event.clientY, gl, camera);
    const doorRotation = door.rotation || 0;
    const mousePosition = new THREE.Vector2(worldX, worldY);
    const doorPosition = new THREE.Vector2(door.position.x, door.position.y);

    // Get mouse position relative to door origin
    const offset = mousePosition.clone().sub(doorPosition);
    // Rotate to door's local coordinate system
    offset.rotateAround(new THREE.Vector2(0, 0), -doorRotation);

    // Calculate the hinge position in local coordinates
    const hingeX = door.flipHorizontal ? renderedDoorWidth : 0;
    const hingeY = 0;

    // Calculate the vector from hinge to mouse in local space
    const dx = (offset.x - hingeX) * flipX;
    const dy = (offset.y - hingeY) * flipY;

    // Calculate angle from hinge to mouse position
    let angle = Math.atan2(dy, dx);

    // Normalize angle to 0 to 2*PI range
    if (angle < 0) angle += 2 * Math.PI;

    // Clamp angle to valid door opening range (0 to 90 degrees)
    if (angle > Math.PI) {
      angle = 0;
    } else if (angle > Math.PI / 2) {
      angle = Math.PI / 2;
    }

    setCurrentOpenAngle(angle);
  };

  const onPointerDownHandle = (event: React.PointerEvent) => {
    event.stopPropagation();
    aboutToModify.current = true;
    setIsDraggingAngle(true);
    const [worldX, worldY] = projectToWorld(event.clientX, event.clientY, gl, camera);
    dragOffset.current = {
      x: worldX - (renderedDoorWidth * 0.75 * Math.cos(currentOpenAngle) * flipX + door.position.x),
      y: worldY - (renderedDoorWidth * 0.75 * Math.sin(currentOpenAngle) * flipY + door.position.y),
    };
    gl.domElement.addEventListener("pointermove", onHandleDrag);
    gl.domElement.addEventListener("pointerup", onPointerUpHandle);
    floorplannerStore.setBlockDirty(true);
  };

  const onPointerUpHandle = () => {
    aboutToModify.current = false;
    setIsDraggingAngle(false);
    if (door.openAngle !== currentOpenAngleRef.current) {
      floorplannerStore.updateSymbolProperty(door.id, "openAngle", currentOpenAngleRef.current);
      floorplannerStore.recordSymbolChange(door.id, 'modify');
      floorplannerStore.pushToUndoStack();
    }
    gl.domElement.removeEventListener("pointermove", onHandleDrag);
    gl.domElement.removeEventListener("pointerup", onPointerUpHandle);
    floorplannerStore.setBlockDirty(false);
    floorplannerStore.setDirty();
  };

  const handleDrag = (newPosition: [number, number]) => {
    onDrag(newPosition);
  };

  const generateArcPoints = useMemo(() => {
    const arcPoints = [];
    const segments = 64;
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * currentOpenAngle;
      arcPoints.push(new THREE.Vector3(
        renderedDoorWidth * Math.cos(angle) * flipX,
        renderedDoorWidth * Math.sin(angle) * flipY,
        0
      ));
    }
    return arcPoints;
  }, [currentOpenAngle, flipX, flipY, renderedDoorWidth]);

  const doorBladePoints = useMemo(() => [
    [0, -bladeThickness, 0] as [number, number, number],
    [renderedDoorWidth * flipX, -bladeThickness, 0] as [number, number, number],
    [renderedDoorWidth * flipX, bladeThickness * flipY - bladeThickness, 0] as [number, number, number],
    [0, bladeThickness * flipY - bladeThickness, 0] as [number, number, number],
    [0, -bladeThickness, 0] as [number, number, number],
  ], [bladeThickness, flipX, flipY, renderedDoorWidth]);

  const doorBladePointsFlipY = useMemo(() => [
    [0, bladeThickness, 0] as [number, number, number],
    [renderedDoorWidth * flipX, bladeThickness, 0] as [number, number, number],
    [renderedDoorWidth * flipX, bladeThickness * flipY + bladeThickness, 0] as [number, number, number],
    [0, bladeThickness * flipY + bladeThickness, 0] as [number, number, number],
    [0, bladeThickness, 0] as [number, number, number],
  ], [bladeThickness, flipX, flipY, renderedDoorWidth]);

  const generateFramePoints = useCallback((wallWidth: number, side: number): [number, number, number][] => {
    const offset = (side * (renderedDoorWidth / 2) + renderedDoorWidth / 2);
    return [
      [offset, -wallWidth / 2, 0],
      [offset + frameThickness * side, -wallWidth / 2, 0],
      [offset + frameThickness * side, wallWidth / 2, 0],
      [offset, wallWidth / 2, 0],
      [offset, -wallWidth / 2, 0], // Closing the frame
    ];
  }, [frameThickness, renderedDoorWidth]);

  const handleResize = (newWidth: number, newHeight: number, boundingBox: BoundingBox, handle: string) => {
    // Calculate the change in bounding box width
    const widthDelta = newWidth - boundingBox.width;

    // Apply the delta to the current door width
    const newDoorWidth = Math.max(doorWidth + widthDelta, 0.1);
    const widthDiff = newDoorWidth - door.doorWidth;

    // Determine which side should stay anchored based on handle and flip state
    // When dragging left handles (topLeft, bottomLeft), the right side should stay fixed
    // When dragging right handles (topRight, bottomRight), the left side should stay fixed
    const isLeftHandle = handle === "topLeft" || handle === "bottomLeft";
    const isRightHandle = handle === "topRight" || handle === "bottomRight";

    // For a door, the origin (position) is at the hinge
    // When flipHorizontal is false, hinge is at left (x=0), door opens to the right
    // When flipHorizontal is true, hinge is at right (x=doorWidth), door opens to the left

    // We need to adjust position to keep the anchored side fixed
    let positionDeltaX = 0;
    if (!door.flipHorizontal) {
      // Hinge at left - if dragging left handles, need to shift position
      if (isLeftHandle) {
        positionDeltaX = -widthDiff;
      }
    } else {
      // Hinge at right - if dragging right handles, need to shift position
      if (isRightHandle) {
        positionDeltaX = widthDiff;
      }
    }

    transaction(() => {
      floorplannerStore.updateSymbolProperty(door.id, "doorWidth", newDoorWidth);

      if (positionDeltaX !== 0) {
        const position = [
          door.position.x + positionDeltaX * Math.cos(door.rotation || 0),
          door.position.y + positionDeltaX * Math.sin(door.rotation || 0),
        ];
        if (attachedToWall) {
          floorplannerStore.detachSymbolFromWall(door.id);
          floorplannerStore.updateSymbolProperty(door.id, "position", position);
          floorplannerStore.attachSymbolToWall(door.id, attachedToWall.id, [position[0], position[1]]);
        } else {
          floorplannerStore.updateSymbolProperty(door.id, "position", position);
        }
      }
    });
  }

  const handleAttachments = () => {
    // Handle attachment logic
  }

  const handlePointerOver = (handle: string) => {
    setHoveringHandle(handle);
  }

  const handlePointerOut = () => {
    setHoveringHandle(null);
  }

  // Update local state of the openAngle if the prop changes
  useEffect(() => {
    setCurrentOpenAngle(door.openAngle);
  }, [door.openAngle]);

  // Update local state of the doorWidth if the prop changes
  useEffect(() => {
    setDoorWidth(door.doorWidth);
  }, [door.doorWidth]);

  // Update the ref whenever currentOpenAngle changes
  useEffect(() => {
    currentOpenAngleRef.current = currentOpenAngle;
  }, [currentOpenAngle]);

  useEffect(() => {
    editorStore.updateGroupRef(door.id, groupRef.current);
  }, [groupRef.current]);

  return (
    <group
      position={[0, 0, getWallAttachmentZOffset(isAttachedToWall, door.zIndex)]}
    >
      {/* Attachment measurement lines - show distance from wall ends while dragging */}
      {attachedToWall && (
        <AttachmentMeasurementLines
          symbol={door}
          attachedWall={attachedToWall}
          isDragging={isDragging}
        />
      )}
      <DraggableObject
        position={[door.position.x, door.position.y]}
        onDragStart={(offset) => {
          setIsDragging(true);
          onDragStart(door, offset)
        }}
        onDragEnd={(endPosition) => {
          onDragEnd(endPosition)
          setIsDragging(false);
        }}
        onDrag={handleDrag}
        selectable={true}
        attachmentId={door.id}
        attachmentType="doorAttachments"
        symbol={door}
      >
        <AttachableSymbol
          attachmentId={door.id}
          attachmentType="doorAttachments"
          onAttachment={handleAttachments}
          store={floorplannerStore}
          ref={attachableRef}
        >
          <SelectableSymbol
            ref={groupRef}
            handleSize={floorplannerStore.symbolHandleSize}
            calculateBoundingBox={calculateBoundingBox}
            onResizeStart={() => setIsResizing(true)}
            onResize={handleResize}
            onResizeEnd={() => setIsResizing(false)}
            center={centerPosition}
            rotation={door.rotation}
            symbol={door}
            onPointerOver={() => {
              handlePointerOver('symbol');
            }}
            onPointerOut={handlePointerOut}
            isDragging={isDragging}
          >
            <group
              ref={setWallAttachmentLayerRef}
              rotation={[0, 0, door.rotation || 0]}
              renderOrder={isAttachedToWall ? wallAttachmentRenderOrder : undefined}
              onPointerEnter={() => {
                if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.add("cursor-vector");
              }}
              onPointerLeave={() => {
                if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.remove("cursor-vector");
              }}
            >
              {/* Invisible box to catch events on any unfilled areas */}
              <mesh
                position={[
                  renderedDoorWidth / 2,
                  (renderedDoorWidth / 2) * flipY,
                  -0.03
                ]}
                rotation={[0, 0, 0]}
                onPointerEnter={() => {
                  if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.add("cursor-vector");
                }}
                onPointerLeave={() => {
                  if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.remove("cursor-vector");
                }}
              >
                <boxGeometry args={[renderedDoorWidth, renderedDoorWidth, 0.06]} />
                <meshBasicMaterial
                  //color="white"
                  transparent
                  opacity={0.0}
                />
              </mesh>
              <ObjectOpacityGroup
                key={`door-opacity-${Math.round(resolveFillOpacity(door) * 100)}-${getOpacityRemountEpoch(door.id)}`}
                opacity={resolveFillOpacity(door)}
              >
              {/* If door is attached to a wall, close the endpoints by drawing a line mesh across the opening with the wall properties */}
              {attachedToWall && (
                <>
                  {/* <mesh position={[(-doorFrameWidth - wallLineWeight), 0, 0]}>
                  <boxGeometry args={[wallLineWeight, wallWidth + wallLineWeight / 2, 0.001]} />
                  <meshStandardMaterial color={wallLineColor} />
                </mesh>
                <mesh position={[(doorWidth + doorFrameWidth + wallLineWeight), 0, 0]}>
                  <boxGeometry args={[wallLineWeight, wallWidth + wallLineWeight / 2, 0.001]} />
                  <meshStandardMaterial color={wallLineColor} />
                </mesh> */}
                </>
              )}
              {/* Render the arc using Line */}
              <Line
                position={new THREE.Vector3(hingePosition[0], hingePosition[1], hingePosition[2])}
                points={generateArcPoints.map((point) => [point.x, point.y, point.z])}
                color={canvasAccentColor(door.selected, hoveringHandle === 'symbol', lineColor)}
                lineWidth={lineWeight / 2} // Fixed pixel size
                {...(door.lineType === "dashed" && {
                  dashed: true,
                  dashSize: 0.15,
                  gapSize: 0.1,
                })}
                raycast={noopRaycast}
              />

              {/* Render door blade using Line */}
              <Line
                points={flipY === 1 ? doorBladePoints : doorBladePointsFlipY}
                color={canvasAccentColor(door.selected, hoveringHandle === 'symbol', lineColor)}
                lineWidth={lineWeight} // Fixed pixel size
                position={new THREE.Vector3(hingePosition[0], hingePosition[1], hingePosition[2])}
                rotation={[0, 0, currentOpenAngle * flipX * flipY]}
                {...(door.lineType === "dashed" && {
                  dashed: true,
                  dashSize: 0.15,
                  gapSize: 0.1,
                })}
                raycast={noopRaycast}
              />

              {/* Render frames if attached to a wall */}
              {attachedToWall && (
                <>
                  {/* Left Frame */}
                  <Line
                    points={generateFramePoints(wallWidth, -1)}
                    color={canvasAccentColor(door.selected, hoveringHandle === 'symbol', lineColor)}
                    lineWidth={1}
                    {...(door.lineType === "dashed" && {
                      dashed: true,
                      dashSize: 0.15,
                      gapSize: 0.1,
                    })}
                    raycast={noopRaycast}
                  />
                  {/* Right Frame */}
                  <Line
                    points={generateFramePoints(wallWidth, 1)}
                    color={canvasAccentColor(door.selected, hoveringHandle === 'symbol', lineColor)}
                    lineWidth={1}
                    {...(door.lineType === "dashed" && {
                      dashed: true,
                      dashSize: 0.15,
                      gapSize: 0.1,
                    })}
                    raycast={noopRaycast}
                  />
                </>
              )}
              </ObjectOpacityGroup>

              {/* Handle for adjusting open angle */}
              {(selectionHandlesVisible && door.selected && !isDragging && !isResizing && editorStore.selections.length === 1) && (
                <group
                  onPointerDown={onPointerDownHandle as unknown as (event: THREE.Event) => void}
                  onPointerUp={onPointerUpHandle as unknown as (event: THREE.Event) => void}
                  onPointerOver={() => handlePointerOver('openHandle')}
                  onPointerOut={handlePointerOut}
                  position={[
                    openAngleGrabHandleX,
                    openAngleGrabHandleY,
                    0,
                  ]}
                >
                  <mesh>
                    <torusGeometry args={[(handleRadius - 0.002), 0.005, 16, 100]} />
                    <meshBasicMaterial color={CANVAS_ACCENT_COLOR} />
                  </mesh>
                  <mesh
                    position={[0, 0, handleDepth / 2]}
                    rotation={[Math.PI / 2, 0, 0]}
                  >
                    <cylinderGeometry args={[(handleRadius - 0.02), (handleRadius - 0.006), handleDepth, 32]} />
                    <meshBasicMaterial color={canvasHandleFillColor(isDraggingAngle, hoveringHandle === "openHandle")} />
                  </mesh>
                </group>
              )}
            </group>
          </SelectableSymbol>
        </AttachableSymbol>
      </DraggableObject>
    </group>
  );
});

export default Door;
